import type { MouseEventHandler, ReactNode } from 'react';
import React from 'react';
import { Link as RRDLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'types/theme.types';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';

export interface LinkProps {
  messageId?: string | ReactNode;
  url?: string;
  plainText?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  download?: string;
  children?: ReactNode;
  openInNewWindow?: boolean;
}

const Link: React.FC<LinkProps> = ({
  messageId,
  url,
  plainText = false,
  onClick,
  className,
  children,
  openInNewWindow,
  ...props
}) => {
  const LinkText = children ? children : plainText ? messageId : <FormattedMessage id={messageId as string} />;

  if (url?.startsWith('/') && !openInNewWindow) {
    return (
      <StyledRRDLink to={url} className={className} {...props} onClick={onClick}>
        {LinkText}
      </StyledRRDLink>
    );
  }
  return (
    <StyledLink
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      href={url}
      onClick={e => {
        e.stopPropagation();
        onClick?.(e);
      }}
      className={className}>
      {LinkText}
    </StyledLink>
  );
};

const StyledLink = styled.a`
  color: ${({ theme }) => theme.tealBlue} !important;
  font-weight: bold;
`;

const StyledRRDLink = styled(RRDLink)`
  color: ${({ theme }) => theme.tealBlue} !important;
`;

export const setLink = (link: string, onClick?: () => void) => buildLink(link, { onClick });

export const buildLink = (link: string, props?: LinkProps) => ({
  a: (text: string | ReactNode) => <Link plainText messageId={text} url={link} {...props} />
});

export const setLinksByTag = (tagToLinkMap: Record<string, string>) => {
  const tagToLinkCallback: Record<string, (text: string | ReactNode) => EmotionJSX.Element> = {};

  Object.keys(tagToLinkMap).forEach(tag => {
    tagToLinkCallback[tag] = text => <Link messageId={text} url={tagToLinkMap[tag] ?? ''} plainText />;
  });

  return tagToLinkCallback;
};

export default Link;
